import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

interface DetailBreadcrumbContextType {
  label: string | undefined;
  setLabel: (label: string | undefined) => void;
}

const DetailBreadcrumbContext = createContext<DetailBreadcrumbContextType | undefined>(undefined);

/**
 * Ponte entre uma página de detalhe e a trilha do header: a trilha é montada só a partir da rota
 * (`useBreadcrumb`), sem acesso ao dado que a própria página busca — aqui é onde esse último nível
 * (ex.: "Fatura" vs "Medição" em `/financeiro/:id`) é publicado para o header ler.
 */
export const DetailBreadcrumbProvider = ({ children }: { children: ReactNode }) => {
  const [label, setLabel] = useState<string | undefined>(undefined);
  const value = useMemo(() => ({ label, setLabel }), [label]);

  return (
    <DetailBreadcrumbContext.Provider value={value}>{children}</DetailBreadcrumbContext.Provider>
  );
};

const useDetailBreadcrumbContext = () => {
  const context = useContext(DetailBreadcrumbContext);
  if (context === undefined) {
    throw new Error('useDetailBreadcrumbContext must be used within a DetailBreadcrumbProvider');
  }
  return context;
};

/** Chamada pela página de detalhe. Limpa ao desmontar, para não vazar para a próxima rota. */
export const useSetDetailBreadcrumbLabel = (label: string | undefined) => {
  const { setLabel } = useDetailBreadcrumbContext();

  useEffect(() => {
    setLabel(label);
    return () => setLabel(undefined);
  }, [label, setLabel]);
};

/** Lida pelo header (`useBreadcrumb`) para completar o último nível da trilha. */
export const useDetailBreadcrumbLabel = (): string | undefined => {
  return useDetailBreadcrumbContext().label;
};
